import React from 'react'
import { TaskService } from '@duck/api-definitions'
import { MultiPick } from '@duck/ui'
import { TranslationKey, useTar } from '@/modules'
import { TASK_PROFICIENCIES } from '@/utils/taskProficiencies'
import { useCanUsePython } from '@/hooks/useCanUsePython'
import { TaskFormField } from './TaskFormField'

const SLUG_REGEX = /^[a-z]+(-[a-z]+)*$/

const DIFFICULTY_OPTIONS: readonly { value: string; label: TranslationKey }[] = [
  { value: 'Easy', label: 'tasks.difficulty.easy' },
  { value: 'Intermediate', label: 'tasks.difficulty.intermediate' },
  { value: 'Hard', label: 'tasks.difficulty.hard' },
  { value: 'Expert', label: 'tasks.difficulty.expert' },
] as const

interface DetailsConfigSectionProps {
  formData: Partial<TaskService.Task>
  onChange: (
    field: keyof TaskService.Task,
    value: string | number | string[] | TaskService.TaskWeaponsOfChoiceEnum[] | undefined,
  ) => void
  fieldErrors?: Record<string, string>
  allAccessGroups: string[]
}

export const DetailsConfigSection: React.FC<DetailsConfigSectionProps> = ({
  formData,
  onChange,
  fieldErrors = {},
  allAccessGroups,
}) => {
  const { t } = useTar()
  const canUsePython = useCanUsePython()

  const weaponOptions = React.useMemo(() => {
    const all = [
      { value: TaskService.TaskWeaponsOfChoiceEnum.Sql, label: t('common.sql') },
      { value: TaskService.TaskWeaponsOfChoiceEnum.Python, label: t('common.python') },
    ]
    return canUsePython ? all : all.filter((o) => o.value === TaskService.TaskWeaponsOfChoiceEnum.Sql)
  }, [t, canUsePython])

  const slugPlaceholder = React.useMemo(() => {
    const name = formData.name ?? ''
    return name
      .replace(/\s+/g, '-')
      .replace(/[^a-zA-Z-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase()
  }, [formData.name])

  const slugValidationError =
    formData.slug != null && formData.slug !== '' && !SLUG_REGEX.test(formData.slug)
      ? t('errors.invalidSlugFormat')
      : null

  const proficiencies = React.useMemo(() => {
    return Object.values(TaskService.TaskProficienciesEnum).map((proficiency) => {
      const proficiencyConfig = TASK_PROFICIENCIES[proficiency]
      return {
        label: t(proficiencyConfig.label),
        value: proficiency,
        icon: proficiencyConfig?.icon(),
      }
    })
  }, [t])

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
          {t('common.basicInformation')}
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <TaskFormField
              label="common.name"
              field="name"
              type="text"
              required
              formData={formData}
              onChange={onChange}
              fieldErrors={fieldErrors}
            />
          </div>
          <div>
            <TaskFormField
              label="tasks.slug"
              field="slug"
              type="text"
              tooltip="Not yet in use"
              formData={formData}
              onChange={onChange}
              fieldErrors={fieldErrors}
              placeholderOverride={slugPlaceholder || undefined}
              errorOverride={
                fieldErrors.slug || slugValidationError || undefined
              }
            />
          </div>
          <div className="lg:col-span-2">
            <TaskFormField
              label="common.teaser"
              field="teaser"
              type="text"
              required
              tooltip="Displayed in Available Challenge card"
              formData={formData}
              onChange={onChange}
              fieldErrors={fieldErrors}
            />
          </div>
          <div className="lg:col-span-2">
            <TaskFormField
              label="common.description"
              field="description"
              type="markdown"
              required
              tooltip="Displayed inside the More Info dialog on a challenge"
              formData={formData}
              onChange={onChange}
              fieldErrors={fieldErrors}
            />
          </div>
        </div>
      </div>

      {/* Configuration */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
          {t('common.configuration')}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <TaskFormField
            label="common.difficulty"
            field="difficulty"
            type="select"
            required
            options={DIFFICULTY_OPTIONS}
            formData={formData}
            onChange={onChange}
            fieldErrors={fieldErrors}
          />
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              {t('common.weapons')}
              <span className="text-error ms-1">*</span>
            </label>
            <MultiPick
              options={weaponOptions}
              selected={formData.allowedWeapons ?? []}
              onChange={(vals) => onChange('allowedWeapons', vals as TaskService.TaskWeaponsOfChoiceEnum[])}
              labels={{ placeholder: t('common.pickWeapons') }}
              disabled={!canUsePython}
            />
            {fieldErrors.allowedWeapons && (
              <p className="text-sm text-error mt-1">{fieldErrors.allowedWeapons}</p>
            )}
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              {t('common.proficiencies')}
            </label>
            <MultiPick
              options={proficiencies}
              selected={formData.proficiencies || []}
              onChange={(vals) => onChange('proficiencies', vals)}
              labels={{ placeholder: t('common.pickProficiencies') }}
            />
            {fieldErrors.proficiencies && (
              <p className="text-sm text-error mt-1">
                {fieldErrors.proficiencies}
              </p>
            )}
          </div>
          <div className="space-y-2" data-testid="task-access-groups">
            <label className="block text-sm font-medium text-foreground">
              {t('common.accessGroups')}
              <span className="text-error ms-1">*</span>
            </label>
            <MultiPick
              options={allAccessGroups.map((g) => ({ label: g, value: g }))}
              selected={
                Array.isArray(formData.accessGroups)
                  ? (formData.accessGroups as string[])
                  : []
              }
              onChange={(vals) => onChange('accessGroups', vals)}
              labels={{ placeholder: t('common.pickAccessGroups') }}
              allowNewItem
            />
            {fieldErrors.accessGroups && (
              <p className="text-sm text-error mt-1">
                {fieldErrors.accessGroups}
              </p>
            )}
          </div>
          <TaskFormField
            label="common.seedFactor"
            field="seedFactor"
            type="number"
            required
            formData={formData}
            onChange={onChange}
            fieldErrors={fieldErrors}
          />
          <TaskFormField
            label="common.availabilityWeight"
            field="availabilityWeight"
            type="number"
            tooltip="Applies to rolling a random challenge"
            formData={formData}
            onChange={onChange}
            fieldErrors={fieldErrors}
          />
          <TaskFormField
            label="tasks.sharedDBName"
            field="sharedDBName"
            type="text"
            formData={formData}
            onChange={onChange}
            fieldErrors={fieldErrors}
          />
          <TaskFormField
            label="tasks.customQueryTimeout"
            field="customQueryTimeout"
            type="number"
            tooltip="Override the default 12 seconds enforced timeout on user queries (up to 256s)"
            formData={formData}
            onChange={onChange}
            fieldErrors={fieldErrors}
          />
        </div>
      </div>
    </div>
  )
}
